import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { TextAndIconField } from '@/components/form/fields/text/TextAndIconInput';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { useCreateCategory } from '@/services/api/hooks/catalog';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import TenantRestrictionTree from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForTenantRestrictions } from '@/components/org/useProcessOrgTreeForTenantRestrictions';

const CreateCategoryFormFields = z.object({
  name: z.object({
    text: z.string(),
    iconId: z.string()
  }),
  isMainDeviceCategory: z.boolean().optional()
});

type CreateCategoryFormFields = z.infer<typeof CreateCategoryFormFields>;

export const CreateCategoryModal: FC<ModalProps> = props => {
  const createCategory = useCreateCategory();
  const methods = useForm<CreateCategoryFormFields>({ resolver: zodResolver(CreateCategoryFormFields) });
  
  const orgTree = useProcessOrgTreeForTenantRestrictions();

  const onSubmit = async (data: CreateCategoryFormFields) => {
    const hiddenFromTenantIds = orgTree.reduce((ids: string[], msp) => {
      if (msp.checked) {
        ids.push(msp.mspId);
      }
      msp.customers.forEach(customer => {
        if (customer.checked) {
          ids.push(customer.customerId);
        }
      });
      return ids;
    }, []);

    await createCategory.mutateAsync({
      name: data.name.text,
      description: data.name.iconId,
      isMainDevice: data.isMainDeviceCategory || false,
      hiddenFromTenantIds
    });
    props.close();
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Create category</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col gap-3 py-2" methods={methods} onSubmit={onSubmit}>
          <TextAndIconField id="name" label="Name" icons={CategoryIcons} />
          <CheckboxField id="isMainDeviceCategory" label="Main Device Category" />
          
          <div className="space-y-1">
            <h2 className="text-lg font-medium">Hide from Tenants</h2>
            <p className="text-sm text-gray-500">Select MSPs or Customers that should NOT see this category.</p>
            <TenantRestrictionTree data={orgTree} />
          </div>

          <Button
            loading={createCategory.isLoading}
            className="w-fit place-self-end"
            variant={ButtonVariant.Primary}
            type="submit"
          >
            Create
          </Button>
        </Form>
      </Card>
    </Modal>
  );
};
